// Quick contact, always within reach without covering the page:
//  - a phone gets one slim bar at the foot (call, WhatsApp, chat, and the
//    page's main action); the page keeps room for it, so nothing is ever
//    stuck underneath;
//  - a desktop gets a small stack of buttons at the corner.
import { lazy, Suspense, useEffect, useState } from 'react';
import { loadChat } from '../lib/chat-store.js';
import { onChat, startLive } from '../lib/chat-live.js';
import { useSite } from '../lib/site-context.jsx';
import { digits, telHref } from '../lib/format.js';
import { Icon, WhatsAppIcon } from './Icon.jsx';

const ChatPanel = lazy(() => import('./ChatPanel.jsx'));

// What the bar's main button does on each page.
function mainAction({ route, path, data, t }) {
  if (route === 'project') {
    return { href: '#enquiry-card', label: data?.project?.status === 'Pre-launch' ? t('dock_waitlist', 'Join waitlist') : t('dock_enquire', 'Enquire') };
  }
  if (route === 'contact' || route === 'visit') return null;
  return { href: path === '/' ? '#enquire' : '/#enquire', label: t('dock_enquire', 'Enquire') };
}

// With the chat closed, a visitor who has asked something still hears back: for a day after their last
// message the page keeps the live connection open, and a reply from the team marks the chat button at once.
const KEEP_LIVE_MS = 24 * 3600 * 1000;
function useTeamReplyWhileClosed(chatOpen) {
  const [unseen, setUnseen] = useState(false);
  useEffect(() => {
    const chat = loadChat();
    if (chat && Date.now() - chat.at < KEEP_LIVE_MS) startLive();
  }, []);
  useEffect(() => {
    if (chatOpen) { setUnseen(false); return undefined; }
    return onChat((event) => {
      if (event.type === 'messages' && event.messages.some((m) => m.role === 'team' && m.id > (loadChat()?.seen || 0))) setUnseen(true);
    });
  }, [chatOpen]);
  return unseen;
}

export function FloatingActions() {
  const site = useSite();
  const { settings, t } = site;
  const [chatOpen, setChatOpen] = useState(false);
  const wa = digits(settings.whatsapp_number);
  const project = site.data?.project;
  const waText = project ? `Hi, I'm interested in ${project.name}` : (settings.whatsapp_default_message || "Hi, I'm interested in your listings");
  const waHref = wa ? `https://wa.me/${wa}?text=${encodeURIComponent(waText)}` : '';
  const tg = settings.telegram_username ? `https://t.me/${encodeURIComponent(settings.telegram_username)}` : '';
  const main = mainAction({ route: site.route, path: site.path, data: site.data, t });
  const toggleChat = () => setChatOpen((o) => !o);
  const reply = useTeamReplyWhileClosed(chatOpen);
  const newReply = reply && <span className="chat-new" data-chat-new title={t('chat_new_reply', 'New reply')} />;

  return (
    <>
      {/* Desktop: a stack at the corner */}
      <div className="fabs" id="kg-fabs">
        {tg && <a id="kg-telegram-button" className="fab telegram-fab" href={tg} target="_blank" rel="noopener noreferrer" aria-label="Chat on Telegram"><Icon name="send" size={22} /><span className="fab-label">Telegram</span></a>}
        {waHref && <a id="kg-whatsapp-button" className="fab whatsapp-fab" href={waHref} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp"><WhatsAppIcon size={24} /><span className="fab-label">WhatsApp</span></a>}
        <button type="button" id="kg-chat-btn" className="fab chat-fab" aria-label={reply ? t('chat_new_reply', 'New reply') : t('chat_open', 'Chat with us')} aria-expanded={chatOpen} aria-controls="kg-chat-panel" onClick={toggleChat}>
          <Icon name={chatOpen ? 'close' : 'chat'} size={22} />
          <span className="fab-label">{t('chat_label', 'Ask us')}</span>
          {newReply}
        </button>
      </div>

      {/* Phone: one bar at the foot */}
      <nav className="dock" aria-label="Contact">
        {settings.general_phone && <a className="dock-btn" href={telHref(settings.general_phone)}><Icon name="phone" size={20} /><span>{t('dock_call', 'Call')}</span></a>}
        {waHref && <a className="dock-btn dock-wa" href={waHref} target="_blank" rel="noopener noreferrer"><WhatsAppIcon size={20} /><span>WhatsApp</span></a>}
        <button type="button" className="dock-btn" aria-expanded={chatOpen} aria-controls="kg-chat-panel" onClick={toggleChat}><Icon name="chat" size={20} /><span>{t('dock_chat', 'Chat')}</span>{newReply}</button>
        {main && <a className="dock-btn dock-main" href={main.href}><span>{main.label}</span></a>}
      </nav>

      {chatOpen && <Suspense fallback={null}><ChatPanel onClose={() => setChatOpen(false)} /></Suspense>}
    </>
  );
}
